import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatMoney as fmtMoney, PAYMENT_METHOD_LABELS_FR } from '@ci-ville/shared';
import { parseDay } from './dates';
// La police standard du PDF n'a pas les espaces insécables du format fr-CA
const formatMoney = (cents) => fmtMoney(cents).replace(/[\u00a0\u202f]/g, ' ');
const d = (v) => (v ? format(parseDay(v), 'd MMMM yyyy', { locale: fr }) : '');
/** Facture PDF conforme (TPS/TVQ, numéros d'inscription, échéance, bon de commande). */
export function exportInvoicePDF(inv) {
    const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    const W = 210;
    const M = 15;
    const issuer = inv.issuer ?? {};
    const draft = inv.status === 'DRAFT';
    const voided = inv.status === 'VOID';
    pdf.setFillColor(33, 150, 243);
    pdf.rect(0, 0, W, 30, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.text(issuer.name ?? 'GEOSIRA INC.', M, 13);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    const issuerLines = [issuer.address, [issuer.email, issuer.phone].filter(Boolean).join(' · ')].filter(Boolean);
    issuerLines.forEach((l, i) => pdf.text(l, M, 19 + i * 4));
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text(draft ? 'FACTURE (BROUILLON)' : 'FACTURE', W - M, 13, { align: 'right' });
    pdf.setFontSize(11);
    pdf.text(draft ? 'Brouillon' : inv.number, W - M, 20, { align: 'right' });
    pdf.setTextColor(20, 20, 20);
    let y = 42;
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Facturé à', M, y);
    pdf.text('Détails', 120, y);
    pdf.setFont('helvetica', 'normal');
    const t = inv.tenant ?? {};
    const billTo = [t.legalName ?? t.name, t.billingContactName, ...(t.billingAddress ? String(t.billingAddress).split('\n') : []), t.billingEmail].filter(Boolean);
    billTo.forEach((l, i) => pdf.text(l, M, y + 5 + i * 4.5));
    const details = [
        ['Date de facturation', d(inv.issueDate)],
        ['Échéance', d(inv.dueDate)],
        ['Période', inv.periodStart ? `${d(inv.periodStart)} au ${d(inv.periodEnd)}` : 'Non précisée'],
        ['Bon de commande', inv.purchaseOrderNumber ?? inv.contract?.purchaseOrderNumber ?? 'Aucun'],
        ['Contrat', inv.contract?.reference ?? 'Aucun'],
    ];
    details.forEach(([k, v], i) => {
        pdf.setTextColor(100, 110, 125);
        pdf.text(k, 120, y + 5 + i * 4.5);
        pdf.setTextColor(20, 20, 20);
        pdf.text(v, W - M, y + 5 + i * 4.5, { align: 'right' });
    });
    y += 12 + Math.max(billTo.length, details.length) * 4.5;
    // Lignes
    pdf.setFillColor(240, 244, 250);
    pdf.rect(M, y - 5, W - 2 * M, 7, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.text('Description', M + 2, y);
    pdf.text('Qté', 130, y, { align: 'right' });
    pdf.text('Prix unitaire', 160, y, { align: 'right' });
    pdf.text('Montant', W - M - 2, y, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    y += 7;
    for (const l of inv.lines) {
        const desc = pdf.splitTextToSize(l.description, 95);
        desc.forEach((line, i) => pdf.text(line, M + 2, y + i * 4.5));
        pdf.text(String(l.quantity), 130, y, { align: 'right' });
        pdf.text(formatMoney(l.unitPriceCents), 160, y, { align: 'right' });
        pdf.text(formatMoney(Math.round(l.quantity * l.unitPriceCents)), W - M - 2, y, { align: 'right' });
        y += desc.length * 4.5 + 3;
        pdf.setDrawColor(230, 234, 240);
        pdf.line(M, y - 2, W - M, y - 2);
    }
    // Totaux
    y += 4;
    const totals = [
        ['Sous-total', inv.subtotalCents],
        [`TPS (5 %)${issuer.tpsNumber ? ` (n° ${issuer.tpsNumber})` : ''}`, inv.tpsCents],
        [`TVQ (9,975 %)${issuer.tvqNumber ? ` (n° ${issuer.tvqNumber})` : ''}`, inv.tvqCents],
        ['Total', inv.totalCents, true],
    ];
    if (inv.paidCents)
        totals.push(['Payé', -inv.paidCents], ['Solde dû', inv.balanceCents, true]);
    for (const [label, cents, bold] of totals) {
        pdf.setFont('helvetica', bold ? 'bold' : 'normal');
        pdf.text(label, 150, y, { align: 'right' });
        pdf.text(formatMoney(cents), W - M - 2, y, { align: 'right' });
        y += 5.5;
    }
    if (inv.payments?.length) {
        y += 4;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Paiements reçus', M, y);
        pdf.setFont('helvetica', 'normal');
        for (const p of inv.payments) {
            y += 5;
            pdf.text(`${d(p.receivedAt)} : ${PAYMENT_METHOD_LABELS_FR[p.method] ?? p.method}${p.reference ? ` (${p.reference})` : ''}`, M, y);
            pdf.text(formatMoney(p.amountCents), W - M - 2, y, { align: 'right' });
        }
    }
    y += 12;
    pdf.setFontSize(8.5);
    pdf.setTextColor(90, 100, 115);
    for (const line of pdf.splitTextToSize(issuer.paymentInstructions ?? '', W - 2 * M)) {
        pdf.text(line, M, y);
        y += 4;
    }
    if (inv.notes) {
        for (const line of pdf.splitTextToSize(inv.notes, W - 2 * M)) {
            y += 4;
            pdf.text(line, M, y);
        }
    }
    if (draft || voided) {
        pdf.setTextColor(220, 38, 38);
        pdf.setFontSize(60);
        pdf.setFont('helvetica', 'bold');
        pdf.text(draft ? 'BROUILLON' : 'ANNULÉE', W / 2, 170, { align: 'center', angle: 30 });
    }
    pdf.save(`${draft ? 'brouillon' : inv.number}.pdf`);
}
//# sourceMappingURL=invoice-pdf.js.map